import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { useToast } from '../context/ToastContext';
import { LogBadge, Spinner } from '../components/UI';

const fmtDate = (d) => new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });

const ACTIONS = ['login','logout','view','copy','add','edit','delete','group_create','group_edit','group_delete','user_create','user_edit','user_delete'];

export default function LogsPage() {
  const toast = useToast();
  const [logs,    setLogs]   = useState([]);
  const [total,   setTotal]  = useState(0);
  const [loading, setLoading]= useState(true);
  const [search,  setSearch] = useState('');
  const [action,  setAction] = useState('');
  const [offset,  setOffset] = useState(0);
  const LIMIT = 100;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset });
      if (search) params.set('user', search);
      if (action) params.set('action', action);
      const { data } = await api.get(`/logs?${params}`);
      setLogs(data.logs);
      setTotal(data.total);
    } catch { toast('Failed to load logs', 'error'); }
    finally  { setLoading(false); }
  }, [search, action, offset, toast]);

  useEffect(() => { setOffset(0); }, [search, action]);
  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    const header = 'Time,User,Action,Entity,Detail\n';
    const rows = logs.map(l =>
      [new Date(l.created_at).toLocaleString(), l.username, l.action, l.entity_name || '', l.detail || '']
        .map(c => `"${String(c).replace(/"/g, '""')}"`)
        .join(',')
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `vaultguard_audit_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast('📥 Exported!');
  };

  return (
    <>
      <div className="topbar">
        <h2>Audit Logs</h2>
        <div className="topbar-right">
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>🔍</span>
            <input
              placeholder="Filter by user…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: 34, width: 200 }}
            />
          </div>
          {/* Action filter */}
          <select
            value={action} onChange={e => setAction(e.target.value)}
            style={{ width: 160 }}
          >
            <option value="">All Actions</option>
            {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={exportCSV}>📥 Export CSV</button>
        </div>
      </div>

      <div className="page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: 13, color: 'var(--sub)' }}>
            Showing {logs.length} of {total} total events
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}>
              ← Prev
            </button>
            <button className="btn btn-ghost btn-sm" disabled={offset + LIMIT >= total} onClick={() => setOffset(o => o + LIMIT)}>
              Next →
            </button>
          </div>
        </div>

        {loading ? <Spinner /> : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Entry / Group</th>
                  <th>Detail</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr><td colSpan={7}>
                    <div className="empty-row"><div className="ei">📋</div>No logs match.</div>
                  </td></tr>
                )}
                {logs.map((l, i) => (
                  <tr key={l.id}>
                    <td className="mono muted small">{offset + i + 1}</td>
                    <td className="mono muted small" style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.created_at)}</td>
                    <td style={{ fontWeight: 700 }}>{l.username}</td>
                    <td><LogBadge action={l.action} /></td>
                    <td style={{ fontSize: 13, maxWidth: 140 }} className="truncate">{l.entity_name || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--sub)', maxWidth: 220 }} className="truncate">{l.detail || '—'}</td>
                    <td className="mono muted small">{l.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
